/* @flow */

import adconfig from '../adconfig';
import adloader from '../adloader';
import containerHelper from '../container-helper';
import _adserverbase from './adserverbase';
import { BrowserUtil, isJSONSupported } from '../utils/browserutil';
import { logDebug, logError } from '../utils/logger';
import isdebugEnabled from '../utils/debug';
import { extend, isFunction, isObject, param } from '../utils/coreutils';
import { getRandom } from '../utils';
import { cleanFrame } from '../utils/iframeutils';
import { filterEnabledSources, getEncryptedIDP } from '../idproviders/idputils';
import { OUT_OF_PAGE_AD_TYPES } from '../constants/outOfPageAdTypes';

interface googleSlot {
  // eslint-disable-next-line no-use-before-define
  addService(googleService): googleSlot;
  setTargeting(string, mixed): googleSlot;
  getSlotElementId(): string;
  setForceSafeFrame(boolean): googleSlot;
  setSafeFrameConfig(mixed): googleSlot;
}

interface googleEvent {
  slot: googleSlot;
  inViewPercentage: number;
}

interface slotRenderEndedEvent {
  slot: googleSlot;
  isEmpty?: boolean;
  size?: string | null;
  creativeId?: string | null;
  lineItemId?: string | null;
  isBackfill?: boolean;
  isProgrammatic?: boolean;
  advertiserId?: string | null;
  campaignId?: string | null;
}

interface googleService {
  disableInitialLoad(): void;
  enableSingleRequest(): boolean;
  getSlots(): Array<googleSlot>;
  setTargeting(string, mixed): googleService;
  addEventListener(string, (googleEvent) => void): googleService;
}

declare type encryptedSignalSource = {
  id: string,
  collectorFunction: () => Promise<string | Array<string>>
};

declare type googleOutOfPageFormat = {
  RIGHT_SIDE_RAIL: string,
  LEFT_SIDE_RAIL: string,
  BOTTOM_ANCHOR: string,
  TOP_ANCHOR: string
};

declare type googleEnums = {
  OutOfPageFormat: googleOutOfPageFormat
};

interface googleGPT {
  secureSignalProviders: Array<encryptedSignalSource>;
  cmd: Array<cbFunc>;
  openConsole(): void;
  display(string | googleSlot): void;
  enableServices(): void;
  destroySlots(?Array<googleSlot>): boolean;
  defineSlot(string, posSizes, string): googleSlot;
  pubads(): googleService;
  defineOutOfPageSlot(string, string): googleSlot;
  enums: googleEnums;
}

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Info to log
 * @property {object} _QP - bid value(cpm) are populated here
 * @property {object} _CV - partner specific pos id
 */

/**
 * Implements Google DFP ad server functionality
 *
 * @class
 * @exports adserver/google
 * @requires  module:coreutils
 * @requires  module:adconfig
 * @requires  module:adloader
 * @requires  module:adserverbase
 * @requires module:containerHelper
 * @requires  module:browserutil
 * @requires module:logger
 */
/**
 * @global
 * @typedef hbPosData
 * @property {string} info Info to log
 * @property {object} _QP - bid value(cpm) are populated here
 * @property {object} _CV - partner specific pos id
 */

/**
 * Implements Google DFP ad server functionality
 *
 * @class
 * @exports adserver/google
 * @requires  module:coreutils
 * @requires  module:adconfig
 * @requires  module:adloader
 * @requires  module:adserverbase
 * @requires module:containerHelper
 * @requires  module:browserutil
 * @requires module:logger
 */

const outOfPageFormatMap = {
  [OUT_OF_PAGE_AD_TYPES.BOTTOM_ANCHOR]: 'BOTTOM_ANCHOR',
  [OUT_OF_PAGE_AD_TYPES.LEFT_SIDE_RAIL]: 'LEFT_SIDE_RAIL',
  [OUT_OF_PAGE_AD_TYPES.RIGHT_SIDE_RAIL]: 'RIGHT_SIDE_RAIL',
  [OUT_OF_PAGE_AD_TYPES.TOP_ANCHOR]: 'TOP_ANCHOR'
};

class _google extends _adserverbase {
  _cVars: { [string]: CustomVars };

  _libAlreadyLoaded: boolean;

  _registeredSignalSources: boolean;

  _oldLib: string;

  _defConfig: googleASConfig;

  _config: googleASConfig;

  _targetToPos: { [string]: string };

  _isGPTInitialized: boolean;

  _eventListenersRegistered: boolean;

  constructor(name: string) {
    super();
    this._name = name;
    this._defConfig = extend(true, {}, this._defConfig, {
      lib: '',
      timeout: 10000,
      url: 'https://securepubads.g.doubleclick.net/gampad/adx?'
    });
    this._config = extend(true, this._defConfig);
    this._cVars = {}; // Store position's custom vars to pass on to ad loader once ad is available
    this._libAlreadyLoaded = false;
    this._registeredSignalSources = false;
    this._oldLib = ''; // Store older lib url to prevent re-loading same lib again after updating config
    this._targetToPos = {};
    this._isGPTInitialized = false;
    this._eventListenersRegistered = false;
  }

  /**
   * Initialize the object - called from adserver initialize
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    super.init(w, d, g);
    // Initialize googletag command queue - needed for library loading pattern
    // Reference: https://developers.google.com/publisher-tag/guides/get-started
    this._win.googletag = this._win.googletag || {
      cmd: [],
      secureSignalProviders: []
    };
    // Event listeners will be registered in _registerEventListeners() only when library is loaded
  }

  /**
   * Register Google Publisher Tag event listeners
   * Only called when library is actually loaded and will be used
   *
   * @returns {undefined}
   * @private
   */
  _registerEventListeners() {
    if (this._eventListenersRegistered) {
      return;
    }

    // $FlowFixMe - window properties for Google
    const win = (this._win: any);
    const gtag: googleGPT = win.googletag;
    if (!gtag || !gtag.cmd) {
      return;
    }

    gtag.cmd.push(() => {
      if (isdebugEnabled() && isFunction(gtag.openConsole)) {
        gtag.openConsole();
      }
      const events = gtag.pubads();
      events.addEventListener('slotRequested', event => {
        // Event is triggered when an ad has been requested for a slot
        const slotID = event.slot.getSlotElementId();
        const position = this._getPosFromTargetId(slotID);
        logDebug(
          `${this._name} requested  slot ID: ${slotID} Pos: ${position}`
        );
        if (position.length) {
          adloader.load(
            position,
            '',
            this._cVars[position],
            this._name,
            false,
            slotID
          );
        }
      });

      // only for debugging
      if (isdebugEnabled()) {
        events.addEventListener('slotResponseReceived', event => {
          // Event is triggered once ad response is available
          const slotID = event.slot.getSlotElementId();
          const position = this._getPosFromTargetId(slotID);
          logDebug(
            `${this._name} response received slot ID: ${slotID} Pos: ${position}`
          );
        });
        events.addEventListener(
          'slotRenderEnded',
          (event: slotRenderEndedEvent) => {
            // Event is triggered once the ad is rendered
            // Reference: https://developers.google.com/publisher-tag/reference#googletag.events.SlotRenderEndedEvent
            const slotID = event.slot.getSlotElementId();
            const position = this._getPosFromTargetId(slotID);
            const {
              isEmpty = false,
              size = 'N/A',
              creativeId = 'N/A',
              lineItemId = 'N/A',
              isBackfill = false,
              isProgrammatic = false
            } = event;
            const isEmptyStr = isEmpty ? 'empty' : 'filled';
            let adType = 'direct';
            if (isBackfill) {
              adType = 'backfill';
            } else if (isProgrammatic) {
              adType = 'programmatic';
            }
            logDebug(
              `${this._name} rendered slot ID: ${slotID} Pos: ${position} Status: ${isEmptyStr} Size: ${size ?? 'N/A'} CreativeID: ${creativeId ?? 'N/A'} LineItemID: ${lineItemId ?? 'N/A'} Type: ${adType}`
            );
          }
        );
        events.addEventListener('slotOnload', event => {
          // Event is triggered  when the creative's iframe fires its load event
          logDebug(
            `${this._name} on loading slot ID : ${event.slot.getSlotElementId()}`
          );
        });
        events.addEventListener('slotVisibilityChanged', event => {
          // Event is triggered when on-screen percentage of an ad slot's area changes
          logDebug(
            `${
              this._name
            }  Visibility Changing slot ID : ${event.slot.getSlotElementId()} Visible area: ${event.inViewPercentage}`
          );
        });
        events.addEventListener('impressionViewable', event => {
          // Event is triggered when an impression becomes viewable
          logDebug(
            `${
              this._name
            }  Impression viewable slot ID : ${event.slot.getSlotElementId()}`
          );
        });
      }
    });

    this._eventListenersRegistered = true;
  }

  /**
   * Check if any position uses googleAS ad server
   *
   * @returns {boolean} - true if at least one position uses googleAS
   * @private
   */
  _hasGooglePositions(): boolean {
    const { settings: { posMap = {} } = {} } = adconfig.get() || {};
    return Object.values(posMap).some((posSettings: mixed) => {
      if (!isObject(posSettings)) {
        return false;
      }
      // $FlowFixMe - posConfigInfo has optional 'as' property
      const posConfig: posConfigInfo = (posSettings: posConfigInfo);
      return posConfig && posConfig.as === this._name;
    });
  }

  // Register encrypted signal sources
  _registerEncryptedSignalSources() {
    const { encryptedSignalSources: essConfig = {} } = this._config || {};
    const { sources = [] } = essConfig;
    if (
      this._registeredSignalSources ||
      !this._libAlreadyLoaded ||
      sources.length === 0 ||
      !isJSONSupported() // need JSON support for encryption
    ) {
      // nothing further to do here
      return;
    }

    // _win.googletag is loaded & available.
    const gtag: googleGPT = this._win.googletag;
    // insert the handlers again
    const o = sources.flatMap(
      ({ source: arrSource = [], encrypt, encryptVersion, uolIDP }) => {
        const arrSources2 = filterEnabledSources(arrSource);
        return arrSources2.map(source => ({
          id: source,
          collectorFunction: () =>
            getEncryptedIDP(source, encrypt, encryptVersion, uolIDP)
        }));
      }
    );
    gtag.cmd.push(() => {
      logDebug(`${this._name} registering Encrypted signals`);
      o.forEach(src => {
        logDebug(`${this._name} registering ESS for ${src.id}`);
        gtag.secureSignalProviders.push(src);
      });
    });

    this._registeredSignalSources = true;
  }

  _preUpdateConfig() {
    // Convert to string for comparison (these ad servers only use single script)
    const libValue = this._config.lib || '';
    this._oldLib = Array.isArray(libValue) ? libValue[0] || '' : libValue;
  }

  _postUpdateConfig() {
    if (!this._config.lib.length) {
      return;
    }

    // Only load library if at least one position uses this ad server
    if (!this._hasGooglePositions()) {
      logDebug(
        `${this._name} no positions use ${this._name}, skipping library load`
      );
      return;
    }

    // load the library only for modern browsers
    if (BrowserUtil.isOldIE()) {
      return;
    }

    // Register event listeners (queued via cmd.push, safe to call before/after library loads)
    this._registerEventListeners();

    // Do not load already loaded library
    // Compare lib value (handle both string and array - these ad servers only use single script)
    const libValueForComparison = Array.isArray(this._config.lib)
      ? this._config.lib[0] || ''
      : this._config.lib || '';
    if (this._libAlreadyLoaded && this._oldLib === libValueForComparison) {
      return;
    }
    this._loadLibrary(this._config.lib);
    this._libAlreadyLoaded = true;
    this._registeredSignalSources = false; // to register again
    this._registerEncryptedSignalSources();
  }

  clearAdRequests() {
    /* _pageName: string,
    _posList: Array<string> = [],
    _bRotation: boolean = false */
    // reset the position specific cvars we cached
    this._cVars = {};
    this._targetToPos = {};
  }

  /**
   * Reverse lookup position name from targetId defined
   *
   * @param {string} slotID - Appnexus response object
   * @returns {string} - Position name
   * @private
   */
  _getPosFromTargetId(slotID: string = ''): string {
    return this._targetToPos[slotID] || '';
  }

  _getadUnitPath(pos: string = ''): string {
    return adconfig.getPosSettings(pos).adUnitPath || '';
  }

  _getSizes(pos: string = ''): posSizes {
    return adconfig.getPosSettings(pos).sizes || [];
  }

  /**
   * Builds iframe tag for each ad position from the qp object
   *
   * @param {string} pos - position
   * @param {object} qp - keywords
   * @returns {string} - Ad creative to render
   * @memberof _google
   */
  _getAdContent(pos: string = '', qp: QPParams = {}): string {
    const iu = this._getadUnitPath(pos);
    const c = getRandom();
    const sizes = this._getSizes(pos);
    const w = adconfig.getPosSettings(pos).width || '';
    const h = adconfig.getPosSettings(pos).height || '';
    const sz = sizes.map(v => v.join('x')).join('|');
    if (iu.length === 0 || sz.length === 0) {
      return '';
    }
    // pass the keywords as query params here
    const t = Object.entries(qp)
      .map(([k, v]) => `${k}=${String(v)}`)
      .join('&');
    const qParams = param({
      iu,
      sz,
      c,
      t
    });

    const url = `${this._config.url}${qParams}`;
    logDebug(`${this._name} IFrame URL: ${url}`);
    return `<iframe src='${url}' frameBorder=0 marginWidth=0 marginHeight=0 scrolling=no allowTransparency topmargin="0" leftmargin="0" backgroundColor="transparant" width=${w} height=${h}></iframe>`;
  }

  /**
   * Load each ad position using old script method
   *
   * @param {string} pos - position
   * @param {object} qp - keywords
   * @memberof _google
   * @returns {undefined}
   */
  loadAdsNoGPT(pos: string, qp: QPParams) {
    const adContent = this._getAdContent(pos, qp);
    if (adContent.length) {
      adloader.load(pos, adContent, this._cVars[pos], '', true);
    } else {
      logDebug(`${this._name} adcontent not avaliable`);
    }
  }

  /**
   *
   * @param {string} containerID containerID goign to be used for ad
   * @returns {boolean}
   */
  _prepareContainerDiv(containerID: string = ''): boolean {
    if (containerID.length === 0) {
      return false;
    }
    const div = this._doc.getElementById(containerID);
    if (!div) {
      return false;
    }
    // cleanup the container div
    cleanFrame(this._doc, div, '');
    return true;
  }

  /**
   * Cleanup GPT slots that are being reused or whose containers no longer exist in DOM.
   * Destroys slots for containers being reused and orphaned slots (containers removed from DOM).
   * Reference: https://developers.google.com/publisher-tag/reference#googletag.destroySlots
   *
   * @param {googleGPT} gtag - Google Publisher Tag instance
   * @param {Object<string, string>} posCIDMap - Map of position names to container IDs
   * @returns {undefined}
   * @private
   */
  _cleanupSlots(gtag: googleGPT, posCIDMap: { [string]: string }) {
    const allSlots = gtag.pubads().getSlots();
    if (allSlots.length === 0) {
      return;
    }

    const targetids = Object.values(posCIDMap);

    // Find slots to destroy: reused containers and orphaned slots
    const slotsToDestroy = allSlots.filter(slot => {
      const containerId = slot.getSlotElementId();
      const isReused = targetids.includes(containerId);
      const containerExists = Boolean(this._doc.getElementById(containerId));
      return isReused || !containerExists;
    });

    if (slotsToDestroy.length > 0) {
      const slotIds = slotsToDestroy.map(s => s.getSlotElementId()).join(',');
      logDebug(`${this._name} Destroying slots: ${slotIds}`);
      gtag.destroySlots(slotsToDestroy);
    }
  }

  _getPageParams(bRotation: boolean): anyObject {
    const arrParams = [this._getQueryParamsCommon(bRotation)];
    return extend(true, {}, ...arrParams);
  }

  /**
   * Loads openx static using adloader
   *
   * @param {Array<string>} posList - position list array
   * @param {object.<string, hbPosData>} o - header bidding return object
   * @param {object} customVars - custom variable to be populated inside iframe
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  loadAds(
    posList: Array<string> = [],
    o: hbPartnerData = {},
    customVars: CustomVars = {},
    bRotation: boolean = false
  ) {
    posList.forEach(pos => {
      this._cVars[pos] = this._getCustomVars(pos, o, customVars, bRotation);
    });
    // data required for defineSlot is created as pos=>object map
    const posCIDMap: { [key: string]: string } = {};
    const displayElements: Array<string | googleSlot> = [];
    const qp: { [key: string]: qparams } = {};
    posList.forEach(pos => {
      // ensure that containers are created before we load ads
      containerHelper.createContainers(pos);
      // Get inactive container div of the ad position
      const adContainerFrameIds: adInfo = containerHelper.getAdContainerInfo(
        pos,
        false
      );
      this._targetToPos[adContainerFrameIds.cID] = pos;
      posCIDMap[pos] = adContainerFrameIds.cID;
      qp[pos] = this._getQueryParamsPosition(pos, o) || {};
    });

    const pageParams = this._getPageParams(bRotation);

    if (this._libAlreadyLoaded) {
      logDebug(`${this._name} queueing up ${posList.join(',')}`);
      logDebug(
        `${this._name} Containers are ${Object.values(posCIDMap).join(',')}`
      );

      // _win.googletag is loaded & available.
      const gtag: googleGPT = this._win.googletag;
      gtag.cmd.push(
        () => {
          this._cleanupSlots(gtag, posCIDMap);
        },
        () => {
          try {
            // set page level targeting before adding positions
            Object.entries(pageParams).forEach(([k, v]) =>
              gtag.pubads().setTargeting(k, v)
            );
            const slots = {};
            posList.forEach(pos => {
              let slot;
              const oopAdType = adconfig.getOutOfPageAdType(pos);
              if (oopAdType.length > 0) {
                slot = gtag.defineOutOfPageSlot(
                  this._getadUnitPath(pos),
                  gtag.enums.OutOfPageFormat[outOfPageFormatMap[oopAdType]]
                );
                // if slot, we need to use this for display only for OOP. Let's override it in posCIDMap
                if (slot) {
                  displayElements.push(slot);
                }
              } else {
                const cID = posCIDMap[pos];
                slot = gtag.defineSlot(
                  this._getadUnitPath(pos),
                  this._getSizes(pos),
                  cID
                );
                this._prepareContainerDiv(cID);
                displayElements.push(cID);
              }
              if (!slot) {
                return;
              }
              slot.addService(gtag.pubads());
              // set targeting
              Object.entries(qp[pos]).forEach(([k, v]) =>
                slot.setTargeting(k, v)
              );
              slots[pos] = slot;
            });

            // enableSingleRequest() and enableServices() should only be called once per page lifecycle
            // Reference: https://developers.google.com/publisher-tag/guides/general-best-practices
            if (!this._isGPTInitialized) {
              gtag.pubads().enableSingleRequest();
              gtag.enableServices();
              this._isGPTInitialized = true;
            }

            displayElements.forEach(ele => gtag.display(ele));
          } catch (error) {
            logError(
              `${this._name} google library calls failure ${error.message}`
            );
          }
        }
      );
    } else {
      logDebug(`${this._name} library not loaded`);
      // fallback to iframe tag for old browser
      posList.forEach(pos => {
        // combine user IDs & QP
        const q = extend(true, {}, qp[pos], pageParams);
        this.loadAdsNoGPT(pos, q);
      });
    }
    adloader.checkQueue();
  }
}

const google: _google = new _google('googleAS');
export default google;
